import React, { Component } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { isInstallerAdmin } from '../../util/permissions';
import { formulaText, formulaVars } from '../../util/areaFormulaDisplay';
import { computeMarineArea, AREA_VAR_LABELS } from '../../util/marineCalc';
import {
  BUILDER_VARS, rowsFromFormula, formulaFromRows, validateRows, stripVars, formulaUsesOnly, SAMPLE_BOAT,
} from '../../util/areaFormulaBuilder';
import FormulaBuilder from './FormulaBuilder';

// GET /api/marine/coverage-defaults renames these two fields on the way out
// (default_area_factor / default_waste_pct); the unprefixed names are kept as
// a fallback so this survives either shape.
const defFactor = (d) => d?.default_area_factor ?? d?.area_factor;
const defWaste = (d) => d?.default_waste_pct ?? d?.waste_pct;

export class AddService extends Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState(props);
  }

  getInitialState = (props) => ({
    // Service definition fields
    serviceName: props.serviceName || "",
    coverageLevel: props.coverageLevel || "",
    // Marine pricing type — 'flat' | 'size' | 'area'. Immutable after create
    // (spec D2). Auto services are always 'size' and never see the select.
    pricingType: props.pricingType || "",
    notes: props.notes || "",
    dataCollectRequired: props.dataCollectRequired || "Y",
    allowMultiple: props.allowMultiple || false,
    taxable: props.taxable !== undefined ? props.taxable : true,

    // Pricing (by vehicle size)
    retailPrices: props.retailPrices || { XL: 0, L: 0, M: 0, S: 0 },
    wholesalePrices: props.wholesalePrices || { XL: 0, L: 0, M: 0, S: 0 },
    durations: props.durations || { XL: 0, L: 0, M: 0, S: 0 },
    expectedQuantities: props.expectedQuantities || { XL: 0, L: 0, M: 0, S: 0 },

    // Use same duration for all sizes (simpler UX)
    useSingleDuration: true,
    singleDuration: props.durations?.M || props.durations?.L || 0,

    // Pricing (area mode)
    retailRate: props.retailRate || "",
    wholesaleRate: props.wholesaleRate || "",
    areaFactor: props.areaFactor ?? "",
    wastePct: props.wastePct ?? "",
    areaDuration: props.areaDuration || "",
    // Measured: entered in this order (spec D3). Unit labels the rates; the
    // checked variables bound the builder; the rows are the formula.
    unitOfMeasure: props.unitOfMeasure || "",
    formulaVarsChecked: props.areaFormula
      ? formulaVars(props.areaFormula).filter((v) => BUILDER_VARS.includes(v))
      : [],
    formulaRows: rowsFromFormula(props.areaFormula),
    presetKey: "",
    materialProductId: props.materialProductId || "",

    // Overlays (service hierarchy)
    // API returns master_service_id for the overridden service
    selectedOverlays: (props.currentOverlays || []).map(o => o.master_service_id),

    // Form state
    errors: {},
    activeTab: 'details'
  });

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      // Modal just opened - reset state based on action
      if (this.props.action === "add") {
        this.setState({
          serviceName: "",
          coverageLevel: "",
          pricingType: this.props.pricingType || "",
          notes: "",
          dataCollectRequired: "Y",
          allowMultiple: false,
          taxable: true,
          retailPrices: { XL: 0, L: 0, M: 0, S: 0 },
          wholesalePrices: { XL: 0, L: 0, M: 0, S: 0 },
          durations: { XL: 0, L: 0, M: 0, S: 0 },
          expectedQuantities: { XL: 0, L: 0, M: 0, S: 0 },
          useSingleDuration: true,
          singleDuration: 0,
          retailRate: "",
          wholesaleRate: "",
          areaFactor: "",
          wastePct: "",
          areaDuration: "",
          unitOfMeasure: "",
          formulaVarsChecked: [],
          formulaRows: [],
          presetKey: "",
          materialProductId: "",
          selectedOverlays: [],
          errors: {},
          activeTab: 'details'
        });
      } else {
        // Update mode - populate with existing data
        // API returns master_service_id for the overridden service
        const currentOverlayIds = (this.props.currentOverlays || []).map(o => o.master_service_id);

        // Check if all durations are the same
        const durations = this.props.durations || { XL: 0, L: 0, M: 0, S: 0 };
        const durationsMatch = durations.L === durations.M && durations.M === durations.S && durations.S === (durations.XL || 0);

        this.setState({
          serviceName: this.props.serviceName || "",
          coverageLevel: this.props.coverageLevel || "",
          pricingType: this.props.pricingType || "",
          notes: this.props.notes || "",
          dataCollectRequired: this.props.dataCollectRequired || "Y",
          allowMultiple: this.props.allowMultiple || false,
          taxable: this.props.taxable !== undefined ? this.props.taxable : true,
          retailPrices: this.props.retailPrices || { XL: 0, L: 0, M: 0, S: 0 },
          wholesalePrices: this.props.wholesalePrices || { XL: 0, L: 0, M: 0, S: 0 },
          durations: durations,
          expectedQuantities: this.props.expectedQuantities || { XL: 0, L: 0, M: 0, S: 0 },
          useSingleDuration: durationsMatch,
          singleDuration: durations.M || durations.L || 0,
          retailRate: this.props.retailRate || "",
          wholesaleRate: this.props.wholesaleRate || "",
          areaFactor: this.props.areaFactor ?? "",
          wastePct: this.props.wastePct ?? "",
          areaDuration: this.props.areaDuration || "",
          unitOfMeasure: this.props.unitOfMeasure || "",
          formulaVarsChecked: this.props.areaFormula
            ? formulaVars(this.props.areaFormula).filter((v) => BUILDER_VARS.includes(v))
            : [],
          formulaRows: rowsFromFormula(this.props.areaFormula),
          presetKey: "",
          materialProductId: this.props.materialProductId || "",
          selectedOverlays: currentOverlayIds,
          errors: {},
          activeTab: 'details'
        });
      }
    }
  }

  handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (type === 'checkbox') {
      this.setState({
        [name]: checked,
        errors: { ...this.state.errors, [name]: '' }
      });
    } else {
      const newState = {
        [name]: value,
        errors: { ...this.state.errors, [name]: '' }
      };

      // Auto-set prices and duration to 0 when service name contains "Custom".
      // "Custom" has no meaning in area mode - a custom service is priced at
      // estimate time, an area service is priced rate x area, and the two
      // mechanisms do not compose.
      if (this.isSizeMode() && name === 'serviceName' && value.toLowerCase().includes('custom')) {
        newState.retailPrices = { XL: 0, L: 0, M: 0, S: 0 };
        newState.wholesalePrices = { XL: 0, L: 0, M: 0, S: 0 };
        newState.durations = { XL: 0, L: 0, M: 0, S: 0 };
        newState.singleDuration = 0;
      }

      this.setState(newState);
    }
  };

  handleDataCollectChange = (event) => {
    this.setState({
      dataCollectRequired: event.target.checked ? "Y" : "N"
    });
  };

  handleUnitChange = (event) => {
    this.setState({ unitOfMeasure: event.target.value, errors: { ...this.state.errors, unitOfMeasure: '' } });
  };

  /** Toggle a variable. Unchecking strips its terms from every row. */
  handleFormulaVarToggle = (name) => {
    this.setState((prev) => {
      const on = prev.formulaVarsChecked.includes(name);
      const checked = on
        ? prev.formulaVarsChecked.filter((v) => v !== name)
        : [...prev.formulaVarsChecked, name];
      const rows = on ? stripVars(prev.formulaRows, checked) : prev.formulaRows;
      return { formulaVarsChecked: checked, formulaRows: rows, errors: { ...prev.errors, formulaRows: '' } };
    });
  };

  handleFormulaRowsChange = (rows) => {
    this.setState({ formulaRows: rows, errors: { ...this.state.errors, formulaRows: '' } });
  };

  /**
   * "Start from" a seeded coverage: REPLACES unit, variables and rows, and
   * seeds factor/waste only where blank. A fill action, not a link — the
   * preset key is not saved.
   */
  handlePresetChange = (event) => {
    const key = event.target.value;
    const def = this.presetDefaults().find((d) => d.coverage_level === key);
    if (!def) { this.setState({ presetKey: "" }); return; }
    const next = {
      presetKey: key,
      unitOfMeasure: def.unit_of_measure === 'ft' || def.unit_of_measure === 'each' ? def.unit_of_measure : 'sq_ft',
      formulaVarsChecked: formulaVars(def.area_formula).filter((v) => BUILDER_VARS.includes(v)),
      formulaRows: rowsFromFormula(def.area_formula),
      errors: { ...this.state.errors, unitOfMeasure: '', formulaRows: '' },
    };
    if (this.state.areaFactor === '') next.areaFactor = String(defFactor(def) ?? 1);
    if (this.state.wastePct === '') next.wastePct = String(defWaste(def) ?? 0);
    this.setState(next);
  };

  /** Seeded coverages the builder can represent (spec §4.4: hull-height presets are omitted). */
  presetDefaults = () =>
    (this.props.coverageDefaults || []).filter((d) => formulaUsesOnly(d.area_formula, BUILDER_VARS));

  handlePriceChange = (priceType, vehicleSize, value) => {
    const priceKey = `${priceType}Prices`;
    const newPrices = {
      ...this.state[priceKey],
      [vehicleSize]: parseFloat(value) || 0
    };

    this.setState({ [priceKey]: newPrices });
  };

  handleExpectedQtyChange = (vehicleSize, value) => {
    this.setState({
      expectedQuantities: {
        ...this.state.expectedQuantities,
        [vehicleSize]: parseFloat(value) || 0
      }
    });
  };

  handleDurationChange = (vehicleSize, value) => {
    if (this.state.useSingleDuration) {
      // Apply to all sizes
      const duration = parseFloat(value) || 0;
      this.setState({
        singleDuration: duration,
        durations: { XL: duration, L: duration, M: duration, S: duration }
      });
    } else {
      // Apply to specific size
      const newDurations = {
        ...this.state.durations,
        [vehicleSize]: parseFloat(value) || 0
      };
      this.setState({ durations: newDurations });
    }
  };

  handleOverlayToggle = (serviceId) => {
    this.setState(prevState => {
      const selected = [...prevState.selectedOverlays];
      const index = selected.indexOf(serviceId);

      if (index > -1) {
        selected.splice(index, 1);
      } else {
        selected.push(serviceId);
      }

      return { selectedOverlays: selected };
    });
  };

  handleSelectAllOverlays = (filteredOverlays) => {
    const allIds = filteredOverlays.map(s => s.id);
    const allSelected = allIds.every(id => this.state.selectedOverlays.includes(id));

    if (allSelected) {
      // Deselect all
      this.setState(prevState => ({
        selectedOverlays: prevState.selectedOverlays.filter(id => !allIds.includes(id))
      }));
    } else {
      // Select all
      this.setState(prevState => ({
        selectedOverlays: [...new Set([...prevState.selectedOverlays, ...allIds])]
      }));
    }
  };

  isMarine = () => this.props.assetType === 'marine';
  /** The effective pricing type. Auto is always size-priced. */
  pricingType = () => (this.isMarine() ? this.state.pricingType : 'size');
  isAreaMode = () => this.pricingType() === 'area';
  isFlatMode = () => this.pricingType() === 'flat';
  isSizeMode = () => this.pricingType() === 'size';

  /**
   * The material products offered for THIS service's category.
   *
   * Matched on `category_name`, NOT on id. Service categories and inventory
   * product categories live in separate tables with independent id sequences,
   * so comparing ids would silently pair a PPF service with a Ceramic product.
   * Name matching is the convention already used for this exact pairing at
   * `newServiceForm.jsx:279`.
   *
   * A product with NO category is kept: it cannot be proven to belong
   * elsewhere, and dropping it would hide inventory from an installer who has
   * simply not categorised it yet. That also means an installer who categorises
   * nothing sees the full list, exactly as before — no regression, and the list
   * narrows on its own as products get categorised.
   */
  categoryProducts = () => {
    const key = (v) => String(v || '').trim().toLowerCase();
    const wanted = key(this.props.categoryName);
    const all = this.props.products || [];
    if (!wanted) return all;
    return all.filter((p) => !p.category_name || key(p.category_name) === wanted);
  };

  /**
   * The fallback chain for a blank factor/waste field, shared by handleSubmit and
   * the worked example so the number on screen is the number that gets saved:
   *
   *   1. what the estimator typed
   *   2. the persisted value this modal was opened with (`props`, not `state` —
   *      state is what the user is currently editing and may have been cleared)
   *   3. unknown — `undefined`, so the caller can omit the key rather than
   *      invent a number
   */
  resolvedAreaFactor = () => {
    if (this.state.areaFactor !== '') return Number(this.state.areaFactor);
    if (this.props.areaFactor !== '' && this.props.areaFactor != null) return Number(this.props.areaFactor);
    return undefined;
  };

  resolvedWastePct = () => {
    if (this.state.wastePct !== '') return Number(this.state.wastePct);
    if (this.props.wastePct !== '' && this.props.wastePct != null) return Number(this.props.wastePct);
    return undefined;
  };

  validateAreaForm = () => {
    const errors = {};
    const num = (v) => (v === '' || v === null ? NaN : Number(v));

    if (!this.state.serviceName?.trim()) errors.serviceName = "Service name is required";
    if (!this.state.notes || !this.state.notes.trim()) errors.notes = "Description is required";

    if (!this.state.unitOfMeasure) errors.unitOfMeasure = "Choose what the rate is per";
    // No standalone "at least one variable checked" rule: validateRows already
    // requires at least one variable (a legacy term's var counts too), so a
    // legacy-only formula (e.g. hull_height_in, manual_area_sqft - both
    // deliberately outside BUILDER_VARS) with nothing ticked still validates,
    // and an editor can save an unrelated change without fabricating a term.
    const rowsCheck = validateRows(this.state.formulaRows, this.state.formulaVarsChecked);
    if (!rowsCheck.ok) errors.formulaRows = rowsCheck.error;

    const retail = num(this.state.retailRate);
    const wholesale = num(this.state.wholesaleRate);
    if (!(retail > 0)) errors.retailRate = "Retail rate is required";
    if (!(wholesale > 0)) errors.wholesaleRate = "Wholesale rate is required";
    // Mirrors the inversion flag ServiceEditView already shows on the size grid.
    // Blocks deliberately: unlike the size grid there is no per-size anchor to notice the inversion against.
    if (retail > 0 && wholesale > 0 && retail < wholesale) {
      errors.retailRate = "Retail is below wholesale — check this is right";
    }

    const duration = num(this.state.areaDuration);
    if (!(duration > 0)) errors.areaDuration = "Duration is required";

    if (this.state.areaFactor !== '' && !(num(this.state.areaFactor) > 0)) {
      errors.areaFactor = "Factor must be greater than 0";
    }
    if (this.state.wastePct !== '') {
      const w = num(this.state.wastePct);
      if (!(w >= 0 && w <= 100)) errors.wastePct = "Waste must be between 0 and 100";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  validateFlatForm = () => {
    const errors = {};
    const num = (v) => (v === '' || v === null ? NaN : Number(v));
    if (!this.state.serviceName?.trim()) errors.serviceName = "Service name is required";
    if (!this.state.notes || !this.state.notes.trim()) errors.notes = "Description is required";
    const retail = num(this.state.retailRate);
    const wholesale = num(this.state.wholesaleRate);
    if (!(retail > 0)) errors.retailRate = "Retail price is required";
    if (!(wholesale > 0)) errors.wholesaleRate = "Wholesale price is required";
    if (retail > 0 && wholesale > 0 && retail < wholesale) {
      errors.retailRate = "Retail is below wholesale — check this is right";
    }
    if (!(num(this.state.areaDuration) > 0)) errors.areaDuration = "Duration is required";
    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  validateForm = () => {
    if (this.isMarine() && !this.state.pricingType) {
      this.setState({ errors: { pricingType: "Pricing type is required" } });
      return false;
    }
    if (this.isAreaMode()) return this.validateAreaForm();
    if (this.isFlatMode()) return this.validateFlatForm();

    const errors = {};
    const serviceName = this.state.serviceName?.trim() || "";
    const isCustomService = serviceName.toLowerCase().includes("custom");

    if (!serviceName) {
      errors.serviceName = "Service name is required";
    }

    // Validate coverage level (required for non-custom services)
    if (!isCustomService && !this.isMarine() && !this.state.coverageLevel) {
      errors.coverageLevel = "Coverage area is required";
    }

    // Validate notes/description (required)
    if (!this.state.notes || this.state.notes.trim() === "") {
      errors.notes = "Description is required";
    }

    const { retailPrices, wholesalePrices } = this.state;

    if (isCustomService) {
      // Custom services MUST have all prices set to 0
      if (retailPrices.L !== 0) {
        errors.retailL = "Custom service price must be 0";
      }
      if (retailPrices.M !== 0) {
        errors.retailM = "Custom service price must be 0";
      }
      if (retailPrices.S !== 0) {
        errors.retailS = "Custom service price must be 0";
      }
      if (retailPrices.XL !== 0) {
        errors.retailXL = "Custom service price must be 0";
      }
      if (wholesalePrices.L !== 0) {
        errors.wholesaleL = "Custom service price must be 0";
      }
      if (wholesalePrices.M !== 0) {
        errors.wholesaleM = "Custom service price must be 0";
      }
      if (wholesalePrices.S !== 0) {
        errors.wholesaleS = "Custom service price must be 0";
      }
      if (wholesalePrices.XL !== 0) {
        errors.wholesaleXL = "Custom service price must be 0";
      }
    } else {
      // Non-custom services require prices > 0
      if (!retailPrices.L || retailPrices.L <= 0) {
        errors.retailL = "Large retail price is required";
      }
      if (!retailPrices.M || retailPrices.M <= 0) {
        errors.retailM = "Medium retail price is required";
      }
      if (!retailPrices.S || retailPrices.S <= 0) {
        errors.retailS = "Small retail price is required";
      }
      if (!retailPrices.XL || retailPrices.XL <= 0) {
        errors.retailXL = "X-Large retail price is required";
      }
      if (!wholesalePrices.L || wholesalePrices.L <= 0) {
        errors.wholesaleL = "Large wholesale price is required";
      }
      if (!wholesalePrices.M || wholesalePrices.M <= 0) {
        errors.wholesaleM = "Medium wholesale price is required";
      }
      if (!wholesalePrices.S || wholesalePrices.S <= 0) {
        errors.wholesaleS = "Small wholesale price is required";
      }
      if (!wholesalePrices.XL || wholesalePrices.XL <= 0) {
        errors.wholesaleXL = "X-Large wholesale price is required";
      }
    }

    // Validate duration
    if (isCustomService) {
      // Custom services MUST have duration set to 0
      if (this.state.useSingleDuration) {
        if (this.state.singleDuration !== 0) {
          errors.duration = "Custom service duration must be 0";
        }
      } else {
        if (this.state.durations.L !== 0) {
          errors.durationL = "Custom service duration must be 0";
        }
        if (this.state.durations.M !== 0) {
          errors.durationM = "Custom service duration must be 0";
        }
        if (this.state.durations.S !== 0) {
          errors.durationS = "Custom service duration must be 0";
        }
        if (this.state.durations.XL !== 0) {
          errors.durationXL = "Custom service duration must be 0";
        }
      }
    } else {
      // Non-custom services require duration > 0
      if (this.state.useSingleDuration) {
        if (!this.state.singleDuration || this.state.singleDuration <= 0) {
          errors.duration = "Duration is required";
        }
      } else {
        if (!this.state.durations.L || this.state.durations.L <= 0) {
          errors.durationL = "Large duration is required";
        }
        if (!this.state.durations.M || this.state.durations.M <= 0) {
          errors.durationM = "Medium duration is required";
        }
        if (!this.state.durations.S || this.state.durations.S <= 0) {
          errors.durationS = "Small duration is required";
        }
        if (!this.state.durations.XL || this.state.durations.XL <= 0) {
          errors.durationXL = "X-Large duration is required";
        }
      }
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = () => {
    if (!this.validateForm()) {
      return;
    }

    if (this.isFlatMode()) {
      const products = this.props.products || [];
      const product = products.find(
        (p) => String(p.product_id) === String(this.state.materialProductId)
      );
      const serviceData = {
        pricingMode: 'flat',
        assetType: this.props.assetType || 'marine',
        serviceName: this.state.serviceName.trim(),
        notes: this.state.notes.trim(),
        dataCollectRequired: this.state.dataCollectRequired,
        allowMultiple: this.state.allowMultiple,
        taxable: this.state.taxable,
        retailRate: Number(this.state.retailRate),
        wholesaleRate: Number(this.state.wholesaleRate),
        durationDays: Number(this.state.areaDuration),
        materialProduct: product
          ? { product_id: product.product_id, product_name: product.product_name || product.name || '', brand: product.brand || '' }
          : null,
        overlays: this.state.selectedOverlays,
      };
      if (this.props.action === "add") this.props.onAdd(serviceData);
      else this.props.onUpdate(serviceData);
      return;
    }

    if (this.isAreaMode()) {
      const products = this.props.products || [];
      const product = products.find(
        (p) => String(p.product_id) === String(this.state.materialProductId)
      );
      // A blank field falls back through resolvedAreaFactor/resolvedWastePct,
      // not a bare 1/0 - "Start from" can re-seed a blank factor/waste from a
      // preset mid-edit, but only while the field is still blank; once the
      // estimator has typed a value (or a persisted one came in via props) a
      // bare fallback must never silently overwrite it with an arbitrary number.
      const areaFactor = this.resolvedAreaFactor();
      const wastePct = this.resolvedWastePct();

      const serviceData = {
        pricingMode: 'area',
        // The container chooses this on the New Service panel. It was hardcoded
        // to 'marine' while area mode and marine were the same thing; they are
        // separate axes in the data model (see marineService.js:44), so read it.
        assetType: this.props.assetType || 'marine',
        serviceName: this.state.serviceName.trim(),
        notes: this.state.notes.trim(),
        dataCollectRequired: this.state.dataCollectRequired,
        // Area-priced services must always permit a second line — each line
        // carries its own calculated area (e.g. two hull-sides on one boat)
        // — regardless of the checkbox's state or whether it even rendered.
        allowMultiple: true,
        taxable: this.state.taxable,
        retailRate: Number(this.state.retailRate),
        wholesaleRate: Number(this.state.wholesaleRate),
        durationDays: Number(this.state.areaDuration),
        unitOfMeasure: this.state.unitOfMeasure,
        areaFormula: formulaFromRows(this.state.formulaRows),
        materialProduct: product
          ? {
              product_id: product.product_id,
              product_name: product.product_name || product.name || '',
              brand: product.brand || '',
            }
          : null,
        overlays: this.state.selectedOverlays,
      };
      // Omitted, not invented: an unknown factor/waste must leave the
      // server's stored value alone, never overwrite it with a substituted
      // 1/0.
      if (areaFactor !== undefined) serviceData.areaFactor = areaFactor;
      if (wastePct !== undefined) serviceData.wastePct = wastePct;

      if (this.props.action === "add") this.props.onAdd(serviceData);
      else this.props.onUpdate(serviceData);
      return;
    }

    // Build durations object
    let durations = this.state.durations;
    if (this.state.useSingleDuration) {
      const d = this.state.singleDuration;
      durations = { XL: d, L: d, M: d, S: d };
    }

    const serviceData = {
      pricingMode: 'size',
      assetType: this.props.assetType || 'auto',
      serviceName: this.state.serviceName.trim(),
      notes: this.state.notes.trim(),
      dataCollectRequired: this.state.dataCollectRequired,
      allowMultiple: this.state.allowMultiple,
      taxable: this.state.taxable,
      retailPrices: this.state.retailPrices,
      wholesalePrices: this.state.wholesalePrices,
      durations: durations,
      expectedQuantities: this.state.expectedQuantities,
      overlays: this.state.selectedOverlays
    };
    // Marine size-priced services have no coverage concept at all - the
    // Coverage Area select is entirely replaced by the Pricing select for
    // isMarine(), so state.coverageLevel is never populated. Omitted, not
    // invented: an absent key leaves the server's stored column untouched,
    // but an explicit null WRITES NULL over it (same convention as
    // areaFactor/wastePct above).
    if (!this.isMarine()) {
      serviceData.coverageLevel = this.state.coverageLevel || null;
    }

    if (this.props.action === "add") {
      this.props.onAdd(serviceData);
    } else {
      this.props.onUpdate(serviceData);
    }
  };

  // Get coverage label for display
  getCoverageLabel = (level) => {
    const labels = {
      'FRONT': 'Front',
      'REAR': 'Rear',
      'SIDE': 'Side',
      'ROOF': 'Roof',
      'FULL': 'Full',
      'INTERIOR': 'Interior',
      'WHEEL': 'Wheel'
    };
    return labels[level] || level;
  };

  /** Flat: one retail price, one wholesale price, one whole-job duration. */
  renderFlatPricing() {
    return (
      <>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label className="small">Retail price <span className="text-danger">*</span></Form.Label>
              <div className="input-group">
                <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                <Form.Control
                  type="number" step="0.01" min="0" name="retailRate"
                  value={this.state.retailRate}
                  onChange={this.handleInputChange}
                  isInvalid={!!this.state.errors.retailRate}
                  placeholder="0.00"
                />
              </div>
              {this.state.errors.retailRate && <small className="text-danger">{this.state.errors.retailRate}</small>}
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="small">Wholesale price <span className="text-danger">*</span></Form.Label>
              <div className="input-group">
                <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                <Form.Control
                  type="number" step="0.01" min="0" name="wholesaleRate"
                  value={this.state.wholesaleRate}
                  onChange={this.handleInputChange}
                  isInvalid={!!this.state.errors.wholesaleRate}
                  placeholder="0.00"
                />
              </div>
              {this.state.errors.wholesaleRate && <small className="text-danger">{this.state.errors.wholesaleRate}</small>}
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label className="small">Duration <span className="text-danger">*</span></Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number" step="0.25" min="0.25" name="areaDuration"
                  value={this.state.areaDuration}
                  onChange={this.handleInputChange}
                  isInvalid={!!this.state.errors.areaDuration}
                  placeholder="1.0"
                />
                <div className="input-group-append"><span className="input-group-text">days</span></div>
              </div>
              {this.state.errors.areaDuration && <small className="text-danger">{this.state.errors.areaDuration}</small>}
              <Form.Text className="text-muted">Per unit — scales with quantity.</Form.Text>
            </Form.Group>
          </Col>
        </Row>
        {this.renderMaterialProduct()}
      </>
    );
  }

  /**
   * Create only. On update the material template already exists and is
   * managed from the service page's "Preferred product(s)" section — a
   * disabled, empty select here would read as data that had been lost.
   * Shared by renderFlatPricing and renderAreaPricing.
   */
  renderMaterialProduct() {
    if (this.props.action !== "add") return null;
    return (
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="small">Default material product</Form.Label>
            <Form.Control
              as="select" name="materialProductId"
              value={this.state.materialProductId}
              onChange={this.handleInputChange}
              isInvalid={!!this.state.errors.materialProductId}
            >
              <option value="">Select...</option>
              {this.categoryProducts().map((p) => (
                <option key={p.product_id} value={p.product_id}>
                  {[p.brand, p.product_name || p.name].filter(Boolean).join(' — ')}
                </option>
              ))}
            </Form.Control>
            {this.state.errors.materialProductId && <small className="text-danger">{this.state.errors.materialProductId}</small>}
            <Form.Text className="text-muted">
              Optional — pre-fills material tracking for jobs using this
              service. Can be set later from the service page.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
    );
  }

  renderAreaPricing() {
    const unit = this.state.unitOfMeasure;
    const uLabel = unit === 'ft' ? 'ft' : unit === 'each' ? 'each' : 'sq ft';
    const rateUnit = `/${uLabel}`;
    const formula = formulaFromRows(this.state.formulaRows);
    const text = formulaText(formula, AREA_VAR_LABELS);
    const vars = formulaVars(formula);
    const presets = this.presetDefaults();

    return (
      <>
        <Form.Group className="mb-3">
          <Form.Label className="small">1. Rate is per <span className="text-danger">*</span></Form.Label>
          <div>
            {[['sq_ft', 'sq ft'], ['ft', 'ft (linear)'], ['each', 'each']].map(([value, label]) => (
              <Form.Check
                inline key={value} type="radio" id={`unit-${value}`} name="unitOfMeasure"
                label={label} value={value}
                checked={unit === value}
                onChange={this.handleUnitChange}
              />
            ))}
          </div>
          {this.state.errors.unitOfMeasure && <small className="text-danger">{this.state.errors.unitOfMeasure}</small>}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="small">2. Variables <span className="text-danger">*</span></Form.Label>
          <div>
            {BUILDER_VARS.map((v) => (
              <Form.Check
                inline key={v} type="checkbox" id={`var-${v}`}
                label={AREA_VAR_LABELS[v] || v}
                checked={this.state.formulaVarsChecked.includes(v)}
                onChange={() => this.handleFormulaVarToggle(v)}
              />
            ))}
          </div>
        </Form.Group>

        <Row className="mb-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label className="small">3. Formula <span className="text-danger">*</span></Form.Label>
              <FormulaBuilder
                rows={this.state.formulaRows}
                allowedVars={this.state.formulaVarsChecked}
                onChange={this.handleFormulaRowsChange}
              />
              {this.state.errors.formulaRows && <small className="text-danger">{this.state.errors.formulaRows}</small>}
            </Form.Group>
          </Col>
          {presets.length > 0 && (
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small">Start from</Form.Label>
                <Form.Control as="select" value={this.state.presetKey} onChange={this.handlePresetChange}>
                  <option value="">Blank</option>
                  {presets.map((d) => (
                    <option key={d.coverage_level} value={d.coverage_level}>
                      {(d.label || '').trim() || d.coverage_level}
                    </option>
                  ))}
                </Form.Control>
                <Form.Text className="text-muted">Fills the unit, variables and rows. Edit freely afterwards.</Form.Text>
              </Form.Group>
            </Col>
          )}
        </Row>

        {text && (
          <div className="alert alert-light border py-2">
            <div className="text-monospace small">{text}</div>
            {vars.length > 0 && (
              <div className="small text-muted">
                Asks the estimator for: {vars.map((v) => AREA_VAR_LABELS[v] || v).join(', ')}
              </div>
            )}
            {this.renderAreaExample(formula, uLabel)}
          </div>
        )}

        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label className="small">Retail rate <span className="text-danger">*</span></Form.Label>
              <div className="input-group">
                <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                <Form.Control
                  type="number" step="0.01" min="0" name="retailRate"
                  value={this.state.retailRate}
                  onChange={this.handleInputChange}
                  isInvalid={!!this.state.errors.retailRate}
                  placeholder="0.00"
                />
                <div className="input-group-append"><span className="input-group-text">{rateUnit}</span></div>
              </div>
              {this.state.errors.retailRate && <small className="text-danger">{this.state.errors.retailRate}</small>}
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="small">Wholesale rate <span className="text-danger">*</span></Form.Label>
              <div className="input-group">
                <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                <Form.Control
                  type="number" step="0.01" min="0" name="wholesaleRate"
                  value={this.state.wholesaleRate}
                  onChange={this.handleInputChange}
                  isInvalid={!!this.state.errors.wholesaleRate}
                  placeholder="0.00"
                />
                <div className="input-group-append"><span className="input-group-text">{rateUnit}</span></div>
              </div>
              {this.state.errors.wholesaleRate && <small className="text-danger">{this.state.errors.wholesaleRate}</small>}
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label className="small">Duration <span className="text-danger">*</span></Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number" step="0.25" min="0.25" name="areaDuration"
                  value={this.state.areaDuration}
                  onChange={this.handleInputChange}
                  isInvalid={!!this.state.errors.areaDuration}
                  placeholder="1.0"
                />
                <div className="input-group-append"><span className="input-group-text">days</span></div>
              </div>
              {this.state.errors.areaDuration && <small className="text-danger">{this.state.errors.areaDuration}</small>}
              <Form.Text className="text-muted">Whole job — not per {uLabel}.</Form.Text>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label className="small">Factor</Form.Label>
              <Form.Control
                type="number" step="0.01" min="0" name="areaFactor"
                value={this.state.areaFactor}
                onChange={this.handleInputChange}
                isInvalid={!!this.state.errors.areaFactor}
                placeholder="1.00"
              />
              {this.state.errors.areaFactor && <small className="text-danger">{this.state.errors.areaFactor}</small>}
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label className="small">Waste</Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number" step="1" min="0" max="100" name="wastePct"
                  value={this.state.wastePct}
                  onChange={this.handleInputChange}
                  isInvalid={!!this.state.errors.wastePct}
                  placeholder="0"
                />
                <div className="input-group-append"><span className="input-group-text">%</span></div>
              </div>
              {this.state.errors.wastePct && <small className="text-danger">{this.state.errors.wastePct}</small>}
            </Form.Group>
          </Col>
        </Row>

        {this.renderMaterialProduct()}
      </>
    );
  }

  /** A worked example on a boat an installer can picture — the cheapest guard against a mistyped factor. */
  renderAreaExample(formula, uLabel) {
    const rate = Number(this.state.retailRate);
    if (!formula || !Number.isFinite(rate) || rate <= 0) return null;
    try {
      const factor = this.resolvedAreaFactor();
      const waste = this.resolvedWastePct();
      const qty = computeMarineArea(
        { area_formula: formula, area_factor: factor === undefined ? 1 : factor, waste_pct: waste === undefined ? 0 : waste },
        SAMPLE_BOAT
      );
      return (
        <div className="small mt-1">
          <strong>Example</strong> — a {SAMPLE_BOAT.loa_ft} ft boat, {SAMPLE_BOAT.beam_ft} ft beam:{' '}
          {qty.toLocaleString('en-US')} {uLabel} @ ${rate.toFixed(2)} ={' '}
          <strong>${(qty * rate).toFixed(2)}</strong> retail
        </div>
      );
    } catch {
      return null;
    }
  }

  render() {
    const { show, onHide, action, categoryName, availableOverlays } = this.props;
    const modalTitle = action === "add" ? "Add Service" : "Update Service";
    const isCustomService = this.state.serviceName?.toLowerCase().includes('custom');

    // Filter available overlays based on coverage compatibility
    const filteredOverlays = (availableOverlays || []).filter(service => {
      const currentCoverage = this.state.coverageLevel;
      const candidateCoverage = service.coverage_level;

      if (!currentCoverage) return false;

      if (currentCoverage === 'FULL') {
        // FULL can overlay FRONT, REAR, SIDE, ROOF, and other FULL
        return candidateCoverage === 'FRONT' || candidateCoverage === 'REAR' ||
               candidateCoverage === 'SIDE' || candidateCoverage === 'ROOF' ||
               candidateCoverage === 'FULL';
      }
      if (currentCoverage === 'FRONT') {
        return candidateCoverage === 'FRONT';
      }
      if (currentCoverage === 'REAR') {
        return candidateCoverage === 'REAR';
      }
      if (currentCoverage === 'SIDE') {
        return candidateCoverage === 'SIDE';
      }
      if (currentCoverage === 'ROOF') {
        return candidateCoverage === 'ROOF';
      }
      if (currentCoverage === 'INTERIOR') {
        return candidateCoverage === 'INTERIOR';
      }
      if (currentCoverage === 'WHEEL') {
        return candidateCoverage === 'WHEEL';
      }
      return false;
    });

    return (
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* Service Definition Section */}
            <div className="border-bottom pb-3 mb-3">
              <h6 className="text-muted mb-3">SERVICE DETAILS</h6>

              <Row className="mb-3">
                <Col md={this.isMarine() ? 4 : 6}>
                  <Form.Group>
                    <Form.Label>Service Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="serviceName"
                      value={this.state.serviceName}
                      onChange={this.handleInputChange}
                      isInvalid={!!this.state.errors.serviceName}
                      placeholder={this.isMarine() ? "e.g., Hull Sides PPF" : "e.g., Full Front End"}
                    />
                    <Form.Control.Feedback type="invalid">
                      {this.state.errors.serviceName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Category</Form.Label>
                    <Form.Control
                      type="text"
                      value={categoryName || ""}
                      disabled
                      className="bg-light"
                    />
                  </Form.Group>
                </Col>
                {this.isMarine() && (
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Asset type</Form.Label>
                      <Form.Control
                        type="text"
                        value={this.props.assetType === 'auto' ? 'Auto' : 'Marine'}
                        disabled
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>
                )}
                {this.isMarine() ? (
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Pricing <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        as="select"
                        name="pricingType"
                        value={this.state.pricingType}
                        onChange={this.handleInputChange}
                        isInvalid={!!this.state.errors.pricingType}
                        disabled={action === "update"}
                      >
                        <option value="">Select...</option>
                        <option value="flat">Flat — one price</option>
                        <option value="size">By size — S / M / L / XL</option>
                        <option value="area">Measured — rate × formula</option>
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {this.state.errors.pricingType}
                      </Form.Control.Feedback>
                      {action === "update" && (
                        <Form.Text className="text-muted">
                          Pricing type is set when the service is created.
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                ) : (
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>
                        Coverage Area {!isCustomService && <span className="text-danger">*</span>}
                      </Form.Label>
                      <Form.Control
                        as="select"
                        name="coverageLevel"
                        value={this.state.coverageLevel}
                        onChange={this.handleInputChange}
                        isInvalid={!!this.state.errors.coverageLevel}
                      >
                        <option value="">Select...</option>
                        <option value="FRONT">Front</option>
                        <option value="REAR">Rear</option>
                        <option value="SIDE">Side</option>
                        <option value="ROOF">Roof</option>
                        <option value="FULL">Full Vehicle</option>
                        <option value="INTERIOR">Interior</option>
                        <option value="WHEEL">Wheel</option>
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {this.state.errors.coverageLevel}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row className="mb-3">
                <Col md={9}>
                  <Form.Group>
                    <Form.Label>Description <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="notes"
                      value={this.state.notes}
                      onChange={this.handleInputChange}
                      isInvalid={!!this.state.errors.notes}
                      placeholder={this.isMarine() ? "e.g., Both hull sides, waterline to rail" : "e.g., Hood, fenders, front bumper, mirrors, headlights"}
                    />
                    <Form.Control.Feedback type="invalid">
                      {this.state.errors.notes}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Form.Group>
                    {/* "Uses materials", not "requires materials collection".
                        This asserts a FACT about the work — a PPF removal
                        consumes nothing, a paint correction consumes nothing —
                        rather than a policy the installer is choosing. How much
                        gets recorded is the shop-level Materials Tracking
                        setting; this only says whether there is anything to
                        record. The old wording read like a second, competing
                        switch over the same decision. */}
                    <Form.Check
                      type="checkbox"
                      id="dataCollectRequired"
                      label="Uses materials"
                      checked={this.state.dataCollectRequired === "Y"}
                      onChange={this.handleDataCollectChange}
                      className="mb-2"
                    />
                    {isInstallerAdmin(this.props.userRole) && (
                      <>
                        <Form.Check
                          type="checkbox"
                          id="allowMultiple"
                          name="allowMultiple"
                          label="Allow multiple quantities"
                          checked={this.isAreaMode() ? true : this.state.allowMultiple}
                          disabled={this.isAreaMode()}
                          onChange={this.handleInputChange}
                        />
                        {this.isAreaMode() && (
                          <Form.Text className="text-muted">
                            Area-priced services always allow multiple lines,
                            because each line carries its own calculated area.
                          </Form.Text>
                        )}
                      </>
                    )}
                    <Form.Check
                      type="checkbox"
                      id="taxable"
                      name="taxable"
                      label="Taxable service"
                      checked={this.state.taxable}
                      onChange={this.handleInputChange}
                      className="mt-1"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Pricing Section */}
            <div className="border-bottom pb-3 mb-3">
              <h6 className="text-muted mb-3">PRICING</h6>

              {this.isAreaMode() && this.renderAreaPricing()}
              {this.isFlatMode() && this.renderFlatPricing()}

              {isCustomService && this.isSizeMode() && (
                <div className="alert alert-info py-2 mb-3">
                  <i className="fa-sharp fa-regular fa-info-circle mr-2"></i>
                  Custom services must have all prices and duration set to 0. These are entered when adding to an estimate.
                </div>
              )}

              {this.isSizeMode() && (
              <>
              {/* Retail Prices */}
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label className="font-weight-bold">Retail Prices <span className="text-danger">*</span></Form.Label>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Small Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.retailPrices.S || ''}
                        onChange={(e) => this.handlePriceChange('retail', 'S', e.target.value)}
                        isInvalid={!!this.state.errors.retailS}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.retailS && (
                      <small className="text-danger">{this.state.errors.retailS}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Medium Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.retailPrices.M || ''}
                        onChange={(e) => this.handlePriceChange('retail', 'M', e.target.value)}
                        isInvalid={!!this.state.errors.retailM}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.retailM && (
                      <small className="text-danger">{this.state.errors.retailM}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Large Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.retailPrices.L || ''}
                        onChange={(e) => this.handlePriceChange('retail', 'L', e.target.value)}
                        isInvalid={!!this.state.errors.retailL}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.retailL && (
                      <small className="text-danger">{this.state.errors.retailL}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">X-Large Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.retailPrices.XL || ''}
                        onChange={(e) => this.handlePriceChange('retail', 'XL', e.target.value)}
                        isInvalid={!!this.state.errors.retailXL}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.retailXL && (
                      <small className="text-danger">{this.state.errors.retailXL}</small>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Wholesale Prices */}
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label className="font-weight-bold">Wholesale Prices <span className="text-danger">*</span></Form.Label>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Small Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.wholesalePrices.S || ''}
                        onChange={(e) => this.handlePriceChange('wholesale', 'S', e.target.value)}
                        isInvalid={!!this.state.errors.wholesaleS}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.wholesaleS && (
                      <small className="text-danger">{this.state.errors.wholesaleS}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Medium Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.wholesalePrices.M || ''}
                        onChange={(e) => this.handlePriceChange('wholesale', 'M', e.target.value)}
                        isInvalid={!!this.state.errors.wholesaleM}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.wholesaleM && (
                      <small className="text-danger">{this.state.errors.wholesaleM}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Large Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.wholesalePrices.L || ''}
                        onChange={(e) => this.handlePriceChange('wholesale', 'L', e.target.value)}
                        isInvalid={!!this.state.errors.wholesaleL}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.wholesaleL && (
                      <small className="text-danger">{this.state.errors.wholesaleL}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">X-Large Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.wholesalePrices.XL || ''}
                        onChange={(e) => this.handlePriceChange('wholesale', 'XL', e.target.value)}
                        isInvalid={!!this.state.errors.wholesaleXL}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.wholesaleXL && (
                      <small className="text-danger">{this.state.errors.wholesaleXL}</small>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Duration */}
              <Row>
                <Col md={12} className="mb-2">
                  <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
                    <Form.Label className="font-weight-bold mb-0">Duration (Days) <span className="text-danger">*</span></Form.Label>
                    <Form.Check
                      type="switch"
                      id="useSingleDuration"
                      name="useSingleDuration"
                      label="Same for all sizes"
                      checked={this.state.useSingleDuration}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </Col>

                {this.state.useSingleDuration ? (
                  <Col md={4}>
                    <Form.Group>
                      <Form.Control
                        type="number"
                        step="0.25"
                        min="0.25"
                        value={this.state.singleDuration || ''}
                        onChange={(e) => this.handleDurationChange('all', e.target.value)}
                        isInvalid={!!this.state.errors.duration}
                        placeholder="0.5"
                      />
                      {this.state.errors.duration && (
                        <small className="text-danger">{this.state.errors.duration}</small>
                      )}
                    </Form.Group>
                  </Col>
                ) : (
                  <>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Small</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.25"
                          min="0.25"
                          value={this.state.durations.S || ''}
                          onChange={(e) => this.handleDurationChange('S', e.target.value)}
                          isInvalid={!!this.state.errors.durationS}
                          placeholder="0.5"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Medium</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.25"
                          min="0.25"
                          value={this.state.durations.M || ''}
                          onChange={(e) => this.handleDurationChange('M', e.target.value)}
                          isInvalid={!!this.state.errors.durationM}
                          placeholder="0.5"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Large</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.25"
                          min="0.25"
                          value={this.state.durations.L || ''}
                          onChange={(e) => this.handleDurationChange('L', e.target.value)}
                          isInvalid={!!this.state.errors.durationL}
                          placeholder="0.5"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">X-Large</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.25"
                          min="0.25"
                          value={this.state.durations.XL || ''}
                          onChange={(e) => this.handleDurationChange('XL', e.target.value)}
                          isInvalid={!!this.state.errors.durationXL}
                          placeholder="0.5"
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>
              </>
              )}

              {/* Expected Material Usage (optional) */}
              {!isCustomService && this.isSizeMode() && (
                <Row className="mt-3">
                  <Col md={12} className="mb-2">
                    <Form.Label className="font-weight-bold mb-0">Expected Material Usage</Form.Label>
                    {this.props.unitLabel && (
                      <small className="text-muted ml-2">({this.props.unitLabel})</small>
                    )}
                    <small className="text-muted d-block">
                      Expected amount of material to complete this service per vehicle size. Used as default when recording materials.
                    </small>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small">Small Vehicle</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        value={this.state.expectedQuantities.S || ''}
                        onChange={(e) => this.handleExpectedQtyChange('S', e.target.value)}
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small">Medium Vehicle</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        value={this.state.expectedQuantities.M || ''}
                        onChange={(e) => this.handleExpectedQtyChange('M', e.target.value)}
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small">Large Vehicle</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        value={this.state.expectedQuantities.L || ''}
                        onChange={(e) => this.handleExpectedQtyChange('L', e.target.value)}
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small">X-Large Vehicle</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        value={this.state.expectedQuantities.XL || ''}
                        onChange={(e) => this.handleExpectedQtyChange('XL', e.target.value)}
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </div>

            {/* Overlays Section - Only show if coverage level is set */}
            {this.state.coverageLevel && filteredOverlays.length > 0 && (
              <div>
                <h6 className="text-muted mb-3">SERVICE OVERLAYS</h6>
                <p className="small text-muted mb-3">
                  Select services that this service includes/overlays. When this service is added to an estimate,
                  the selected services below will be automatically removed.
                </p>

                <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {/* Select All Checkbox */}
                  <Form.Check
                    type="checkbox"
                    id="overlay-select-all"
                    className="mb-2 pb-2 border-bottom font-weight-bold"
                    label="Select All"
                    checked={filteredOverlays.length > 0 && filteredOverlays.every(s => this.state.selectedOverlays.includes(s.id))}
                    onChange={() => this.handleSelectAllOverlays(filteredOverlays)}
                  />

                  {filteredOverlays.map(service => (
                    <Form.Check
                      key={service.id}
                      type="checkbox"
                      id={`overlay-${service.id}`}
                      className="mb-2"
                      label={
                        <span>
                          {service.service_name}
                          <span className="badge badge-secondary ml-2">
                            {this.getCoverageLabel(service.coverage_level)}
                          </span>
                          {service.notes && (
                            <small className="text-muted d-block">{service.notes}</small>
                          )}
                        </span>
                      }
                      checked={this.state.selectedOverlays.includes(service.id)}
                      onChange={() => this.handleOverlayToggle(service.id)}
                    />
                  ))}
                </div>

                {this.state.selectedOverlays.length > 0 && (
                  <small className="text-info mt-2 d-block">
                    {this.state.selectedOverlays.length} service(s) will be auto-removed when this service is selected
                  </small>
                )}
              </div>
            )}

            {/* Overlays do not apply to area-priced services at all: coverage
                compatibility is a hardcoded chain over the seven AUTOMOTIVE
                coverages, so a marine coverage never matches and this branch
                fired on every single one — announcing "No other measured_area
                coverage services available" as though something were missing. */}
            {this.isSizeMode() && !this.isMarine() && this.state.coverageLevel && filteredOverlays.length === 0 && (
              <div className="text-muted small">
                <i className="fa-sharp fa-regular fa-info-circle mr-2"></i>
                No other {this.getCoverageLabel(this.state.coverageLevel).toLowerCase()} coverage services available for overlay configuration.
              </div>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" onClick={this.handleSubmit}>
            {action === "add" ? "Add Service" : "Update Service"}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
